<script setup>
// SiteUp: Central de Agendamento — painel que abre por cima de qualquer tela
// (card do Hub / item Agenda do Modo Foco; flag agenda_central, hoje só a Lia).
// Mostra o mês, a lista de horários disponíveis do dia e cria compromisso
// manual (com ou sem contato do Chatwoot) via ScheduleModal. O envio de
// WhatsApp continua sendo o fluxo já existente da conversa/kanban — nada aqui
// mexe nessa automação.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useLocale } from 'shared/composables/useLocale';
import LeadAppointmentsAPI from 'dashboard/api/leadAppointments';
import AgentAPI from 'dashboard/api/agents';
import { useAlert } from 'dashboard/composables';
import ScheduleModal from './ScheduleModal.vue';
import { sendAppointmentNotice } from './appointmentNotice';
import {
  addDays,
  buildDayTimeline,
  endOfDay,
  formatMinutes,
  isoDate,
  minutesOfDate,
  monthGridStart,
  startOfDay,
} from './agendaHelpers';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const SLOT_STEP = 30;

const { t } = useI18n();
// pt_BR (underscore) não é tag válida pro Intl; o composable normaliza.
const { resolvedLocale } = useLocale();
const router = useRouter();

// Relogio da sessao do painel: `today` era congelado no setup, entao o corte
// de "agora" envelhecia com o painel aberto. Ambos sao renovados a cada
// abertura (watch de modelValue).
const now = ref(new Date());
const today = ref(startOfDay(new Date()));
const monthAnchor = ref(
  new Date(today.value.getFullYear(), today.value.getMonth(), 1)
);
const selectedDay = ref(today.value);
const appointments = ref([]);
const agents = ref([]);
const loading = ref(false);
const responsibleFilter = ref('all');
const scheduleOpen = ref(false);
const scheduleSaving = ref(false);
const scheduleError = ref('');
const schedulePrefill = ref(null);

const close = () => emit('update:modelValue', false);

const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1);

// --- calendário do mês ---------------------------------------------------

const gridStart = computed(() => monthGridStart(monthAnchor.value));

// Só a inicial: `capitalize` do CSS deixaria "Agosto De 2026" em pt-BR.
const monthLabel = computed(() =>
  capitalize(
    monthAnchor.value.toLocaleDateString(resolvedLocale.value, {
      month: 'long',
      year: 'numeric',
    })
  )
);

const weekdayLetters = computed(() => {
  const sunday = new Date(2023, 0, 1);
  return Array.from({ length: 7 }, (_, index) =>
    addDays(sunday, index)
      .toLocaleDateString(resolvedLocale.value, { weekday: 'narrow' })
      .toUpperCase()
  );
});

const calendarCells = computed(() =>
  Array.from({ length: 42 }, (_, index) => {
    const date = addDays(gridStart.value, index);
    return {
      date,
      key: isoDate(date),
      day: date.getDate(),
      inMonth: date.getMonth() === monthAnchor.value.getMonth(),
      isToday: isoDate(date) === isoDate(today.value),
      isSelected: isoDate(date) === isoDate(selectedDay.value),
    };
  })
);

const shiftMonth = delta => {
  monthAnchor.value = new Date(
    monthAnchor.value.getFullYear(),
    monthAnchor.value.getMonth() + delta,
    1
  );
};

const pickDay = cell => {
  selectedDay.value = startOfDay(cell.date);
  if (!cell.inMonth) {
    monthAnchor.value = new Date(
      cell.date.getFullYear(),
      cell.date.getMonth(),
      1
    );
  }
};

// --- dados ----------------------------------------------------------------

const mapAppointment = raw => {
  const scheduled = raw?.scheduled_at ? new Date(raw.scheduled_at) : null;
  const valid = scheduled && !Number.isNaN(scheduled.getTime());
  const duration = Number(raw?.duration_min);
  return {
    id: raw.id,
    // Cartao agendado direto no Kanban chega com kind 'kanban_item': sem isso
    // um card id 5 e um compromisso id 5 dividiriam a mesma chave de lista.
    kind: raw.kind || 'appointment',
    dayKey: valid ? isoDate(scheduled) : '',
    startMin: valid ? minutesOfDate(scheduled) : 0,
    durationMin: duration > 0 ? duration : 30,
    status: raw.status,
    title: raw.contact?.name || raw.description || '',
    description: raw.description || '',
    location: raw.location || '',
    origin: raw.origin,
    responsibleId: raw.responsible?.id ?? null,
    responsibleName: raw.responsible?.name || '',
  };
};

const fetchMonth = async () => {
  loading.value = true;
  try {
    const { data } = await LeadAppointmentsAPI.list({
      from: gridStart.value.toISOString(),
      to: endOfDay(addDays(gridStart.value, 41)).toISOString(),
    });
    appointments.value = (Array.isArray(data) ? data : []).map(mapAppointment);
  } catch {
    appointments.value = [];
    useAlert(t('KANBAN.AGENDA_QUICK.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const loadAgents = async () => {
  try {
    const { data } = await AgentAPI.get();
    agents.value = Array.isArray(data) ? data : [];
  } catch {
    agents.value = [];
  }
};

const busyDayKeys = computed(() => {
  const keys = new Set();
  appointments.value.forEach(item => {
    if (item.dayKey && item.status !== 'cancelled') keys.add(item.dayKey);
  });
  return keys;
});

const dayItems = computed(() => {
  const key = isoDate(selectedDay.value);
  return appointments.value.filter(item => {
    if (item.dayKey !== key) return false;
    if (item.status === 'cancelled') return false;
    if (
      responsibleFilter.value !== 'all' &&
      Number(item.responsibleId) !== Number(responsibleFilter.value)
    ) {
      return false;
    }
    return true;
  });
});

const events = computed(() =>
  [...dayItems.value].sort((a, b) => a.startMin - b.startMin)
);

// --- horários disponíveis -------------------------------------------------

const freeRanges = computed(() =>
  buildDayTimeline(dayItems.value).filter(row => row.type === 'free')
);

// Dia anterior a hoje nao oferece vaga, e no dia de hoje o corte e o relogio:
// sem isto a central oferecia 08:00 as 12h e deixava marcar no passado.
const isPastDay = computed(
  () => isoDate(selectedDay.value) < isoDate(today.value)
);
const isToday = computed(
  () => isoDate(selectedDay.value) === isoDate(today.value)
);
// Meia hora de antecedencia, o mesmo contrato do modal da conversa
// (KanbanAppointmentModal): "daqui a 5 minutos" nao e vaga util.
const LEAD_TIME_MIN = 30;
const notBeforeMin = computed(() =>
  isToday.value ? minutesOfDate(now.value) + LEAD_TIME_MIN : null
);

const slotTimes = computed(() => {
  if (isPastDay.value) return [];
  const out = [];
  freeRanges.value.forEach(range => {
    const first = Math.ceil(range.startMin / SLOT_STEP) * SLOT_STEP;
    for (let min = first; min + SLOT_STEP <= range.endMin; min += SLOT_STEP) {
      if (notBeforeMin.value != null && min < notBeforeMin.value) continue; // eslint-disable-line no-continue
      out.push({ startMin: min, freeMin: range.endMin - min });
    }
  });
  return out;
});

// Linha do tempo do dia: livre e ocupado na ordem real (o que o operador lê).
// Cada faixa livre carrega os primeiros horários clicáveis; a primeira ganha o
// selo "próxima" e casa com o card de próxima vaga no topo.
const MAX_ROW_SLOTS = 4;

const dayRows = computed(() => {
  let markedNext = false;
  return buildDayTimeline(dayItems.value).map(row => {
    if (row.type !== 'free')
      return { ...row, key: `ev-${row.item.kind}-${row.item.id}` };
    const chips = slotTimes.value
      .filter(
        slot => slot.startMin >= row.startMin && slot.startMin < row.endMin
      )
      .slice(0, MAX_ROW_SLOTS);
    // O selo "proxima" acompanha a primeira faixa que ainda TEM vaga: numa
    // faixa inteira no passado ele apontava para um horario nao clicavel.
    const isNext = !markedNext && chips.length > 0;
    if (isNext) markedNext = true;
    return { ...row, key: `free-${row.startMin}`, chips, isNext };
  });
});

// Próxima vaga do card de topo: primeiro horário clicável (já alinhado ao passo).
const nextSlot = computed(() => slotTimes.value[0] || null);

const shortDur = minutes => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours && rest) return `${hours}h${String(rest).padStart(2, '0')}`;
  if (hours) return `${hours}h`;
  return `${rest} min`;
};

const dayTitle = computed(() =>
  capitalize(
    selectedDay.value.toLocaleDateString(resolvedLocale.value, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
    })
  )
);

const summaryLabel = computed(() => {
  const count = events.value.length;
  const free = shortDur(
    freeRanges.value.reduce(
      (total, range) => total + (range.endMin - range.startMin),
      0
    )
  );
  return count === 1
    ? t('KANBAN.AGENDA_QUICK.SUMMARY_ONE', { free })
    : t('KANBAN.AGENDA_QUICK.SUMMARY_MANY', { count, free });
});

const rangeLabel = item =>
  `${formatMinutes(item.startMin)} – ${formatMinutes(item.startMin + item.durationMin)}`;

const originLabel = origin => {
  if (origin === 'google') return t('KANBAN.AGENDA_QUICK.ORIGIN_GOOGLE');
  if (origin === 'kanban') return t('KANBAN.AGENDA_QUICK.ORIGIN_CHATWOOT');
  return t('KANBAN.AGENDA_QUICK.ORIGIN_MANUAL');
};

const originClass = origin => {
  if (origin === 'google') return 'bg-n-blue-9/15 text-n-blue-11';
  if (origin === 'kanban') return 'bg-n-teal-9/15 text-n-teal-11';
  return 'bg-n-amber-9/15 text-n-amber-11';
};

const originBorder = origin => {
  if (origin === 'google') return 'border-l-n-blue-9';
  if (origin === 'kanban') return 'border-l-n-teal-9';
  return 'border-l-n-amber-9';
};

const dotClass = origin => {
  if (origin === 'google') return 'bg-n-blue-9';
  if (origin === 'kanban') return 'bg-n-teal-9';
  return 'bg-n-amber-9';
};

const eventMeta = item => {
  const range = rangeLabel(item);
  return item.responsibleName ? `${range} · ${item.responsibleName}` : range;
};

const quickDays = computed(() =>
  [0, 1].map(offset => ({
    offset,
    label:
      offset === 0
        ? t('KANBAN.AGENDA_BOARD.TODAY')
        : t('KANBAN.AGENDA_BOARD.TOMORROW'),
    date: addDays(today.value, offset),
    selected:
      isoDate(addDays(today.value, offset)) === isoDate(selectedDay.value),
  }))
);

const pickQuickDay = chip => {
  selectedDay.value = chip.date;
  monthAnchor.value = new Date(
    chip.date.getFullYear(),
    chip.date.getMonth(),
    1
  );
};

// --- criação --------------------------------------------------------------

const openSchedule = (startMin = null) => {
  schedulePrefill.value = { date: selectedDay.value, startMin };
  scheduleError.value = '';
  scheduleOpen.value = true;
};

const openNew = () => {
  openSchedule(slotTimes.value[0]?.startMin ?? null);
};

const submitSchedule = async payload => {
  if (scheduleSaving.value) return;
  scheduleSaving.value = true;
  scheduleError.value = '';
  try {
    await LeadAppointmentsAPI.create({
      scheduled_at: payload.scheduledAt,
      duration_min: payload.durationMin,
      description: payload.title,
      location: payload.location,
      responsible_user_id: payload.responsibleId,
      contact_id: payload.contactId || undefined,
      linked_kanban_item_id: payload.kanbanItemId || undefined,
      // Sem isto o select "Agenda de destino" do formulario seria enfeite aqui:
      // a escolha morria na Central e o backend caia na conexao do funil.
      ...(payload.googleCalendarConnectionId
        ? { google_calendar_connection_id: payload.googleCalendarConnectionId }
        : {}),
    });
    // Conferencia da etapa 2: o aviso sai depois da gravacao dar certo.
    await sendAppointmentNotice(payload);
    scheduleOpen.value = false;
    await fetchMonth();
    useAlert(t('KANBAN.AGENDA_BOARD.SAVED'));
  } catch {
    scheduleError.value = t('KANBAN.AGENDA_BOARD.SAVE_ERROR');
    useAlert(scheduleError.value);
  } finally {
    scheduleSaving.value = false;
  }
};

const openFullAgenda = () => {
  close();
  router.push({ name: 'kanban_board', query: { view: 'agenda' } });
};

watch(gridStart, (next, previous) => {
  if (!previous || isoDate(next) !== isoDate(previous)) fetchMonth();
});

watch(
  () => props.modelValue,
  open => {
    if (!open) return;
    now.value = new Date();
    today.value = startOfDay(now.value);
    selectedDay.value = today.value;
    monthAnchor.value = new Date(
      today.value.getFullYear(),
      today.value.getMonth(),
      1
    );
    responsibleFilter.value = 'all';
    fetchMonth();
    if (!agents.value.length) loadAgents();
  }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[2147482000] flex justify-end bg-n-alpha-black2 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <div
        class="flex h-full w-[920px] max-w-full flex-col overflow-y-auto border-l border-n-weak bg-n-solid-1"
        data-testid="agenda-quick-panel"
      >
        <div
          class="flex items-center justify-between border-b border-n-weak px-5 py-4"
        >
          <h2
            class="m-0 flex items-center gap-2 text-[17px] font-medium text-n-slate-12"
          >
            <span
              class="i-ri-calendar-2-line size-5 text-n-teal-11"
              aria-hidden="true"
            />
            {{ t('KANBAN.AGENDA_QUICK.TITLE') }}
          </h2>
          <button
            type="button"
            class="flex size-8 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
            :aria-label="t('KANBAN.AGENDA_BOARD.CLOSE')"
            @click="close"
          >
            <span class="i-ri-close-line size-5" aria-hidden="true" />
          </button>
        </div>

        <div class="grid flex-1 md:grid-cols-[280px_1fr]">
          <!-- trilho: calendário + filtros -->
          <aside class="border-b border-n-weak p-4 md:border-b-0 md:border-r">
            <div
              class="rounded-2xl border border-n-weak bg-n-solid-2 p-3"
              data-testid="agenda-quick-calendar"
            >
              <div class="flex items-center justify-between px-1">
                <button
                  type="button"
                  class="flex size-7 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                  :aria-label="t('KANBAN.AGENDA_QUICK.PREV_MONTH')"
                  @click="shiftMonth(-1)"
                >
                  <span
                    class="i-ri-arrow-left-s-line size-5"
                    aria-hidden="true"
                  />
                </button>
                <span class="text-[13px] font-medium text-n-slate-12">
                  {{ monthLabel }}
                </span>
                <button
                  type="button"
                  class="flex size-7 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                  :aria-label="t('KANBAN.AGENDA_QUICK.NEXT_MONTH')"
                  @click="shiftMonth(1)"
                >
                  <span
                    class="i-ri-arrow-right-s-line size-5"
                    aria-hidden="true"
                  />
                </button>
              </div>
              <div class="mt-2 grid grid-cols-7 gap-0.5">
                <span
                  v-for="(letter, index) in weekdayLetters"
                  :key="`dow-${index}`"
                  class="pb-1 text-center text-[10px] font-medium text-n-slate-10"
                >
                  {{ letter }}
                </span>
              </div>
              <div class="grid grid-cols-7 gap-0.5">
                <button
                  v-for="cell in calendarCells"
                  :key="cell.key"
                  type="button"
                  class="relative flex h-8 items-center justify-center rounded-lg text-[12.5px] font-semibold tabular-nums [@media(pointer:coarse)]:min-h-11"
                  :class="[
                    cell.inMonth ? 'text-n-slate-12' : 'text-n-slate-8',
                    cell.isSelected
                      ? 'bg-n-teal-9 font-medium text-white'
                      : cell.isToday
                        ? 'bg-n-alpha-2 font-medium'
                        : 'hover:bg-n-alpha-2',
                  ]"
                  @click="pickDay(cell)"
                >
                  {{ cell.day }}
                  <span
                    v-if="busyDayKeys.has(cell.key)"
                    class="absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full"
                    :class="cell.isSelected ? 'bg-white' : 'bg-n-teal-11'"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>

            <div class="mt-3 flex flex-wrap gap-1.5">
              <button
                v-for="chip in quickDays"
                :key="chip.offset"
                type="button"
                class="h-8 rounded-full px-3 text-[12.5px] font-bold [@media(pointer:coarse)]:min-h-11"
                :class="
                  chip.selected
                    ? 'bg-n-slate-12 text-n-solid-1'
                    : 'border border-n-weak text-n-slate-12 hover:bg-n-solid-2'
                "
                @click="pickQuickDay(chip)"
              >
                {{ chip.label }}
              </button>
            </div>

            <p
              v-if="agents.length"
              class="mb-1.5 mt-4 text-[10.5px] font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ t('KANBAN.AGENDA_BOARD.RESPONSIBLE') }}
            </p>
            <div
              v-if="agents.length"
              class="flex flex-wrap gap-1.5"
              data-testid="agenda-quick-filter"
            >
              <button
                type="button"
                class="h-7 rounded-full px-2.5 text-[11.5px] font-bold [@media(pointer:coarse)]:min-h-11"
                :class="
                  responsibleFilter === 'all'
                    ? 'bg-n-blue-9/15 text-n-blue-11'
                    : 'border border-n-weak text-n-slate-11'
                "
                @click="responsibleFilter = 'all'"
              >
                {{ t('KANBAN.AGENDA_QUICK.ALL_RESPONSIBLES') }}
              </button>
              <button
                v-for="agent in agents"
                :key="agent.id"
                type="button"
                class="h-7 rounded-full px-2.5 text-[11.5px] font-bold [@media(pointer:coarse)]:min-h-11"
                :class="
                  Number(responsibleFilter) === Number(agent.id)
                    ? 'bg-n-blue-9/15 text-n-blue-11'
                    : 'border border-n-weak text-n-slate-11'
                "
                @click="responsibleFilter = agent.id"
              >
                {{ agent.name }}
              </button>
            </div>

            <button
              type="button"
              class="mt-4 flex h-9 w-full items-center justify-center gap-1.5 rounded-full text-[12.5px] font-bold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="openFullAgenda"
            >
              <span
                class="i-ri-external-link-line size-3.5 shrink-0"
                aria-hidden="true"
              />
              {{ t('KANBAN.AGENDA_QUICK.OPEN_FULL') }}
            </button>
          </aside>

          <!-- dia selecionado -->
          <section class="p-5" data-testid="agenda-quick-day">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 class="m-0 text-[17px] font-medium text-n-slate-12">
                  {{ dayTitle }}
                </h3>
                <p class="m-0 mt-0.5 text-[12.5px] text-n-slate-11">
                  {{ summaryLabel }}
                </p>
              </div>
              <button
                type="button"
                class="h-9 rounded-full bg-n-blue-9 px-4 text-[13px] font-bold text-white hover:bg-n-blue-10"
                data-testid="agenda-quick-new"
                @click="openNew"
              >
                {{ t('KANBAN.AGENDA_QUICK.NEW') }}
              </button>
            </div>

            <p
              v-if="loading"
              class="m-0 py-8 text-center text-[13px] text-n-slate-10"
            >
              {{ t('KANBAN.AGENDA_BOARD.LOADING') }}
            </p>
            <template v-else>
              <!-- Próxima vaga em destaque (atalho pra marcar rápido) -->
              <button
                v-if="nextSlot"
                type="button"
                class="mt-3 flex w-full items-center gap-3.5 rounded-2xl border-[1.5px] border-n-teal-9/50 bg-gradient-to-br from-n-teal-9/15 to-n-teal-9/5 px-4 py-3.5 text-left hover:border-n-teal-9/70"
                data-testid="agenda-quick-next"
                @click="openSchedule(nextSlot.startMin)"
              >
                <span
                  class="text-[30px] font-extrabold leading-none tabular-nums text-n-teal-11"
                >
                  {{ formatMinutes(nextSlot.startMin) }}
                </span>
                <span class="flex-1">
                  <span
                    class="block text-[10.5px] font-medium uppercase tracking-wide text-n-teal-11"
                  >
                    {{ t('KANBAN.AGENDA_QUICK.NEXT_SLOT') }}
                  </span>
                  <span class="mt-0.5 block text-[13px] text-n-slate-11">
                    {{
                      t('KANBAN.AGENDA_QUICK.NEXT_SLOT_FREE', {
                        free: shortDur(nextSlot.freeMin),
                      })
                    }}
                  </span>
                </span>
                <span
                  class="h-10 shrink-0 rounded-full bg-n-teal-9 px-[18px] text-[13.5px] font-medium leading-10 text-white"
                >
                  {{ t('KANBAN.AGENDA_QUICK.BOOK') }}
                </span>
              </button>

              <!-- O dia em ordem: livre e ocupado juntos -->
              <div class="mb-1 mt-[18px] flex items-center gap-2.5">
                <span
                  class="text-[10px] font-medium uppercase tracking-wide text-n-slate-10"
                >
                  {{ t('KANBAN.AGENDA_QUICK.THE_DAY') }}
                </span>
                <span class="h-px flex-1 bg-n-weak" aria-hidden="true" />
              </div>

              <div
                v-if="!dayRows.length"
                class="rounded-xl border border-dashed border-n-weak px-4 py-6 text-center text-[12.5px] text-n-slate-10"
              >
                {{ t('KANBAN.AGENDA_QUICK.EMPTY_DAY') }}
              </div>
              <div
                v-else
                class="relative mt-2 pl-[60px]"
                data-testid="agenda-quick-timeline"
              >
                <span
                  class="absolute bottom-1 left-[52px] top-1 w-0.5 bg-n-weak"
                  aria-hidden="true"
                />
                <div
                  v-for="row in dayRows"
                  :key="row.key"
                  class="relative mb-1.5"
                >
                  <span
                    class="absolute -left-[60px] top-2.5 w-11 pr-1 text-right text-[11px] font-extrabold tabular-nums text-n-slate-10"
                  >
                    {{ formatMinutes(row.startMin) }}
                  </span>

                  <template v-if="row.type === 'free'">
                    <span
                      class="absolute -left-3.5 top-3 size-[9px] rounded-full border-2 border-n-solid-1 bg-n-solid-1"
                      aria-hidden="true"
                    />
                    <div
                      class="flex items-center gap-3 rounded-xl border-[1.5px] px-3.5 py-2"
                      :class="
                        row.isNext
                          ? 'border-solid border-n-teal-9/60 bg-n-teal-9/10'
                          : 'border-dashed border-n-teal-9/40 bg-n-teal-9/5'
                      "
                    >
                      <span
                        class="flex items-center whitespace-nowrap text-[12.5px] font-extrabold tabular-nums text-n-teal-11"
                      >
                        {{
                          t('KANBAN.AGENDA_QUICK.FREE_RANGE', {
                            range: `${formatMinutes(row.startMin)}–${formatMinutes(row.endMin)}`,
                          })
                        }}
                        <span
                          v-if="row.isNext"
                          class="ml-1.5 rounded-full border border-n-teal-9/50 px-1.5 py-px text-[9px] font-medium uppercase tracking-wide text-n-teal-11"
                        >
                          {{ t('KANBAN.AGENDA_QUICK.NEXT_TAG') }}
                        </span>
                      </span>
                      <span class="ml-auto flex flex-wrap justify-end gap-1.5">
                        <button
                          v-for="chip in row.chips"
                          :key="chip.startMin"
                          type="button"
                          class="h-7 rounded-full border border-n-teal-9/35 bg-n-teal-9/20 px-2.5 text-[12px] font-extrabold tabular-nums text-n-teal-11 hover:border-n-teal-9 hover:bg-n-teal-9 hover:text-white [@media(pointer:coarse)]:min-h-11"
                          data-testid="agenda-quick-slot"
                          @click="openSchedule(chip.startMin)"
                        >
                          {{ formatMinutes(chip.startMin) }}
                        </button>
                      </span>
                    </div>
                  </template>

                  <template v-else>
                    <span
                      class="absolute -left-3.5 top-3 size-[9px] rounded-full border-2 border-n-solid-1"
                      :class="dotClass(row.item.origin)"
                      aria-hidden="true"
                    />
                    <div
                      class="flex items-center gap-2.5 rounded-xl border border-n-weak border-l-[3px] bg-n-solid-2 px-3 py-2"
                      :class="originBorder(row.item.origin)"
                    >
                      <span class="min-w-0 flex-1">
                        <span
                          class="block truncate text-[13px] font-bold text-n-slate-12"
                        >
                          {{ row.item.title }}
                        </span>
                        <span class="block text-[11.5px] text-n-slate-11">
                          {{ eventMeta(row.item) }}
                        </span>
                      </span>
                      <span
                        class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium"
                        :class="originClass(row.item.origin)"
                      >
                        {{ originLabel(row.item.origin) }}
                      </span>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </section>
        </div>
      </div>
    </div>
  </Teleport>

  <ScheduleModal
    v-model="scheduleOpen"
    :saving="scheduleSaving"
    :save-error="scheduleError"
    :items="appointments"
    :agents="agents"
    :prefill="schedulePrefill"
    :locale="resolvedLocale"
    elevated
    guard-conflicts
    @submit="submitSchedule"
  />
</template>
